// Progressive enhancement for the blog's category pills.
//
// The pills are real links to /blogs/<handle>/tagged/<tag>, so with JS off they
// navigate and everything works. This intercepts the click, fetches the same
// section through the Section Rendering API and swaps the markup in place — the
// point being that the visitor keeps their scroll position instead of landing
// back at the top of the page on every filter change.
//
// Deliberately mirrors collection-filters.js: no client-side filter state, the
// server decides what matches, and the URL stays shareable.
class BlogFilters extends HTMLElement {
  connectedCallback() {
    // Listeners live on this element and survive the innerHTML swap, so binding
    // them per render would stack a duplicate every time — the same defect that
    // made the cart add three of everything.
    if (this.hasSetUp) return;
    this.hasSetUp = true;

    this.addEventListener('click', (event) => {
      const pill = event.target.closest('.filter-pill');
      if (!pill) return;
      // Let modified clicks do what the visitor asked: new tab, new window.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      this.load(pill.getAttribute('href'), true);
    });

    // Back and forward must retrace the filter steps, not reload the page.
    window.addEventListener('popstate', () => this.load(window.location.href, false));
  }

  async load(url, push) {
    if (!url || this.busy) return;
    this.busy = true;
    this.setAttribute('aria-busy', 'true');

    try {
      const target = new URL(url, window.location.origin);
      const fetchUrl = new URL(target);
      fetchUrl.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(fetchUrl, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('blog-filters');
      if (!fresh) throw new Error('no section in response');

      this.innerHTML = fresh.innerHTML;
      if (push) window.history.pushState({}, '', target);
    } catch (error) {
      // Anything unexpected — offline, a 404, a changed section id — falls back
      // to a normal navigation rather than leaving the visitor on a stale list.
      window.location.href = url;
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('blog-filters')) {
  customElements.define('blog-filters', BlogFilters);
}
